import React, {
	useState, useEffect, useCallback, useRef, useContext
} from 'react';
import classNames from 'classnames';

import { AuthContext } from '../../context/auth-context';
import { PRACTITIONER_STATUS } from '../../data/constants';

import Link from '../Link';
import NavMobileMenu from '../NavMobileMenu';
import renderIcon from '../../utils/render-icon';

import { getMessagingForProtectedContent } from '../../utils/messaging';

const ns = `navigation`;
const menuControlIds = {
	learn: 'navigation-dropdown-learn',
	mobile: 'mobile-navigation-menu',
	shop: 'navigation-dropdown-shop',
	why: 'navigation-dropdown-why',
};

const Navigation = ({
	pathname,
	hideDropdowns,
	menuItems = [],
	menuCard = {},
	location
}) => {
	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	const { state: authState } = useContext(AuthContext);
	const { practitioner } = authState;
	const practitionerStatus = practitioner.status || '';

	const isUserStatusActive = practitionerStatus.toUpperCase() === PRACTITIONER_STATUS.ACTIVE;

	const navItems = {
		shop: [],
		whyNordic: [],
		learn: [],
	};

	const replaceAmpersand = string => {
		return string.replace(/&amp;/g, '&');
	};

	let menuIndex;

	menuIndex = 0;
	if (menuItems[menuIndex]) {
		navItems.shop = [{
			title: menuItems[menuIndex].label,
			highlight: menuItems[menuIndex].highlightFirstLink,
			links: menuItems[menuIndex].links
				.map(el => {
					return {
						label: replaceAmpersand(el.link.title),
						url: el.link.url,
						target: el.link.target,
					};
				}),
		}];

		menuItems[menuIndex].megaMenu.forEach(el => {
			navItems.shop.push({
				title: el.label,
				links: el.links.map(el2 => {
					return {
						label: replaceAmpersand(el2.link.title),
						url: el2.link.url,
						target: el2.link.target,
					};
				}),
			});
		});
	}

	menuIndex = 1;
	if (menuItems[menuIndex]) {
		navItems.learn = [{
			title: menuItems[menuIndex].label,
			links: menuItems[menuIndex].links.map(el => {
				return {
					label: replaceAmpersand(el.link.title),
					url: el.link.url,
					target: el.link.target,
				};
			}),
		}];
	}

	menuIndex = 2;
	if (menuItems[menuIndex]) {
		navItems.whyNordic = [{
			title: menuItems[menuIndex].label,
			links: menuItems[menuIndex].links.map(el => {
				return {
					label: replaceAmpersand(el.link.title),
					url: el.link.url,
					target: el.link.target,
				};
			}),
		}];
	}

	const [activeMenu, setActiveMenu] = useState('');
	const [dropdownHeight, setDropdownheight] = useState(0);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [hoverMod, setHoverMod] = useState(false);
	const [hoverNavigationEnabled, setHoverNavigationEnabled] = useState(true);
	const navRef = useRef();
	const dropdownContentRefs = useRef({});
	const menuButtonRefs = useRef({});
	const firstLinkRefs = useRef({});

	const updateDropdownHeight = useCallback(menu => {
		const dropdownContent = dropdownContentRefs.current[menu];

		if (!dropdownContent) {
			setDropdownheight(0);
			return;
		}

		setDropdownheight(dropdownContent.offsetHeight);
	}, []);

	const closeMenu = useCallback(() => {
		setActiveMenu('');
		setDropdownheight(0);
		setHoverMod(false);
	}, []);

	const openMenu = useCallback((menu, applyHoverMod = false) => {
		setActiveMenu(menu);
		setHoverMod(menu === 'shop' && applyHoverMod);
		updateDropdownHeight(menu);
	}, [updateDropdownHeight]);

	const focusFirstMenuLink = useCallback(menu => {
		window.requestAnimationFrame(() => {
			firstLinkRefs.current[menu]?.focus();
		});
	}, []);

	const toggleMenu = useCallback((menu, applyHoverMod = false) => {
		if (menu === activeMenu) {
			closeMenu();
			return;
		}

		openMenu(menu, applyHoverMod);
	}, [activeMenu, closeMenu, openMenu]);

	const handleMenuKeyDown = useCallback((event, menu) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			openMenu(menu, menu === 'shop');
			focusFirstMenuLink(menu);
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			closeMenu();
		}
	}, [closeMenu, focusFirstMenuLink, openMenu]);

	const handleDropdownKeyDown = useCallback((event, menu) => {
		if (event.key !== 'Escape') {
			return;
		}

		event.preventDefault();
		closeMenu();
		menuButtonRefs.current[menu]?.focus();
	}, [closeMenu]);

	const handleMenuBlur = useCallback((event, menu) => {
		if (activeMenu !== menu) {
			return;
		}

		const nextFocusedElement = event.relatedTarget;

		if (
			nextFocusedElement
			&& event.currentTarget.contains(nextFocusedElement)
		) {
			return;
		}

		closeMenu();
	}, [activeMenu, closeMenu]);

	const handleNavClosure = useCallback(event => {
		if (!navRef.current.contains(event.target)) {
			closeMenu();
		}
	}, [closeMenu]);

	useEffect(() => {
		const isTouchDevice = () => {
			return 'ontouchstart' in window || navigator.maxTouchPoints;
		};
		if (isTouchDevice()) {
			setHoverNavigationEnabled(false);
		} else {
			setHoverNavigationEnabled(true);
		}
	}, []);

	useEffect(() => {
		if (hideDropdowns) {
			closeMenu();
		}
	}, [closeMenu, hideDropdowns]);

	useEffect(() => {
		closeMenu();
	}, [closeMenu, pathname]);

	useEffect(() => {
		window.addEventListener('click', handleNavClosure);

		return () => {
			window.removeEventListener('click', handleNavClosure);
		};
	}, [handleNavClosure]);

	useEffect(() => {
		if (!activeMenu) {
			return;
		}

		updateDropdownHeight(activeMenu);
	}, [activeMenu, updateDropdownHeight]);

	useEffect(() => {
		const handleResize = () => {
			if (activeMenu) {
				updateDropdownHeight(activeMenu);
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [activeMenu, updateDropdownHeight]);

	const openMenuHandler = () => {
		setMobileMenuOpen(true);
		document.body.classList.add('modal-open');
	};

	const closeMenuHandler = () => {
		setMobileMenuOpen(false);
		document.body.classList.remove('modal-open');
	};

	return (
		<nav
			aria-label="Main"
			className={`${ rootClassnames } navStyle`}
			ref={navRef}
			onMouseLeave={() => {
				setActiveMenu('');
			}}
		>
			<div
				className={`${ ns }__option`}
				onBlur={event => {
					handleMenuBlur(event, 'shop');
				}}
			>
				<button
					ref={node => {
						menuButtonRefs.current.shop = node;
					}}
					aria-controls={menuControlIds.shop}
					aria-expanded={activeMenu === 'shop'}
					aria-haspopup="menu"
					onClick={() => {
						toggleMenu('shop', true);
					}}
					onKeyDown={event => {
						handleMenuKeyDown(event, 'shop');
					}}
					onMouseEnter={() => {
						hoverNavigationEnabled && openMenu('shop', true);
					}}
				>
					Shop
					{renderIcon('carrot-down')}
				</button>
				<div
					id={menuControlIds.shop}
					className={`${ ns }__dropdown-container shop ${ activeMenu === 'shop' ? 'active' : '' }`}
					aria-hidden={activeMenu !== 'shop'}
					style={{
						height: activeMenu === 'shop' ? dropdownHeight : 0,
					}}
					onKeyDown={event => {
						handleDropdownKeyDown(event, 'shop');
					}}
					onMouseEnter={() => {
						setHoverMod(false);
					}}
				>
					<div
						className={`${ ns }__dropdown`}
						ref={node => {
							dropdownContentRefs.current.shop = node;
						}}
					>
						<div className={`${ ns }__dropdown-content`}>
							<div className={navItems.shop[0].highlight ? 'product-type highlight-first' : 'product-type'}>
								{navItems.shop[0] && navItems.shop[0].links.map((value, index) => {
									return (
										<Link
											key={value.label}
											className="product-type-nav-link"
											ref={node => {
												if (index === 0) {
													firstLinkRefs.current.shop = node;
												}
											}}
											tabIndex={activeMenu === 'shop' ? 0 : -1}
											to={value.url}
											sameTab={value.target && value.target !== '_blank'}
											variant="navy"
										>
											{value.label}
										</Link>
									);
								})}
							</div>
							<div className="health-intrest">
								{navItems.shop.map((value, index) => {
									if (index > 0) {
										return (
											<div className="health-intrest-column" key={value.title}>
												<p className="health-intrest-title">{value.title}</p>
												{value.links.map(link => {
													return (
														<Link
															className="nav-link"
															tabIndex={activeMenu === 'shop' ? 0 : -1}
															to={link.url}
															sameTab={value.target && value.target !== '_blank'}
															key={link.label}
															variant="navy"
														>
															{link.label}
														</Link>
													);
												})}
											</div>
										);
									}
									return true;
								})}
							</div>
							<div className="card">
								<div className="card-container">
									{menuCard.eyebrow && <p>{menuCard.eyebrow}</p>}
									{menuCard.headline && <h5>{menuCard.headline}</h5>}
									{menuCard.cta?.url && menuCard.cta?.title && (
										<Link
											tabIndex={activeMenu === 'shop' ? 0 : -1}
											to={`/products/nordic-naturals${menuCard.cta.url}`}
										>
											{menuCard.cta.title}
										</Link>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div
				className={`${ ns }__option`}
				onBlur={event => {
					handleMenuBlur(event, 'learn');
				}}
			>
				<button
					ref={node => {
						menuButtonRefs.current.learn = node;
					}}
					aria-controls={menuControlIds.learn}
					aria-expanded={activeMenu === 'learn'}
					aria-haspopup="menu"
					onClick={() => {
						toggleMenu('learn');
					}}
					onKeyDown={event => {
						handleMenuKeyDown(event, 'learn');
					}}
					onMouseEnter={() => {
						hoverNavigationEnabled && openMenu('learn');
					}}
				>
					Learn
					{renderIcon('carrot-down')}
				</button>
				<div
					id={menuControlIds.learn}
					className={`${ ns }__dropdown-container learn ${ activeMenu === 'learn' ? 'active' : '' }`}
					aria-hidden={activeMenu !== 'learn'}
					style={{
						height: activeMenu === 'learn' ? dropdownHeight : 0,
					}}
					onKeyDown={event => {
						handleDropdownKeyDown(event, 'learn');
					}}
				>
					<div
						className={`${ ns }__dropdown`}
						ref={node => {
							dropdownContentRefs.current.learn = node;
						}}
					>
						{navItems.learn[0] && navItems.learn[0].links.map((link, index) => {
							let linkUrl = link.url;
							let clickHandler;

							//	--------------------------
							//	Protected Content Handling
							//	--------------------------
							const isProtectedContent = /gateway\.on24\.com/.test(linkUrl);

							if (isProtectedContent && !isUserStatusActive) {
								const message = getMessagingForProtectedContent(false, true);
								linkUrl = '#';
								clickHandler = () => {
									// eslint-disable-next-line no-alert
									window.alert(message);
								};
							}

							return (
								<div className={`${ ns }__dropdown-link`} key={link.label}>
									<Link
										ref={node => {
											if (index === 0) {
												firstLinkRefs.current.learn = node;
											}
										}}
										tabIndex={activeMenu === 'learn' ? 0 : -1}
										to={linkUrl}
										sameTab={link.target && link.target !== '_blank'}
										variant="navy"
										onClick={clickHandler}
									>
										{link.label}
									</Link>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			<div
				className={`${ ns }__option`}
				onBlur={event => {
					handleMenuBlur(event, 'why');
				}}
			>
				<button
					ref={node => {
						menuButtonRefs.current.why = node;
					}}
					aria-controls={menuControlIds.why}
					aria-expanded={activeMenu === 'why'}
					aria-haspopup="menu"
					className={hoverMod ? 'why-button-mod' : ''}
					onClick={() => {
						toggleMenu('why');
					}}
					onKeyDown={event => {
						handleMenuKeyDown(event, 'why');
					}}
					onMouseEnter={() => {
						hoverNavigationEnabled && openMenu('why');
						setHoverMod(false);
					}}
				>
					Why Nordic?
					{renderIcon('carrot-down')}
				</button>
				<div
					id={menuControlIds.why}
					className={`${ ns }__dropdown-container why-nordic ${ activeMenu === 'why' ? 'active' : '' }`}
					aria-hidden={activeMenu !== 'why'}
					style={{
						height: activeMenu === 'why' ? dropdownHeight : 0,
					}}
					onKeyDown={event => {
						handleDropdownKeyDown(event, 'why');
					}}
				>
					<div
						className={`${ ns }__dropdown`}
						ref={node => {
							dropdownContentRefs.current.why = node;
						}}
					>
						{navItems.whyNordic[0] && navItems.whyNordic[0].links.map((link, index) => {
							return (
								<div className={`${ ns }__dropdown-link`} key={link.label}>
									<Link
										ref={node => {
											if (index === 0) {
												firstLinkRefs.current.why = node;
											}
										}}
										tabIndex={activeMenu === 'why' ? 0 : -1}
										to={link.url}
										sameTab={link.target && link.target !== '_blank'}
										variant="navy"
									>
										{link.label}
									</Link>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			<div className={`${ ns }__option-mobile`}>
				<button
					aria-controls={menuControlIds.mobile}
					aria-expanded={mobileMenuOpen}
					aria-haspopup="dialog"
					aria-label={mobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
					onClick={() => {
						if (mobileMenuOpen) {
							closeMenuHandler();
						} else {
							openMenuHandler();
						}
					}}
				>
					<div />
					<div />
					<div />
				</button>
			</div>
			<NavMobileMenu
				id={menuControlIds.mobile}
				open={mobileMenuOpen}
				onCloseCallback={closeMenuHandler}
				menuItems={navItems}
				location={location}
			/>
		</nav>
	);
};

export default Navigation;
